import { ImportStrategyProps } from './types';
import { useAuctionItemImportStrategy } from 'components/imports/useAuctionItemImportStrategy';
import { useCompanyImportStrategy } from 'components/imports/useCompanyImportStrategy';
import { useContactActivityImportStrategy } from 'components/imports/useContactActivityImportStrategy';
import { useContactImportStrategy } from 'components/imports/useContactImportStrategy';
import { useFundImportStrategy } from 'components/imports/useFundImportStrategy';
import { useRecurringPlanImportStrategy } from 'components/imports/useRecurringPlanImportStrategy';
import { useSoftCreditImportStrategy } from 'components/imports/useSoftCreditImportStrategy';
import { useTransactionImportStrategy } from 'components/imports/useTransactionImportStrategy';
import { ImportType } from 'types/domain';

/**
 * Each import implements the ImportStrategy interface.
 *
 * To create a new import:
 * - add the new import to the ImportType type
 * - create a new hook that returns your new ImportStrategy
 * - register your new ImportStrategy below
 */

const useImportStrategy = <T extends ImportType>(value: T, props: ImportStrategyProps<T> = {}) => {
    return getImportStrategy(value)(props);
};

const getImportStrategy = (strategy: ImportType) => {
    switch (strategy) {
        case 'contacts':
            return useContactImportStrategy;
        case 'companies':
            return useCompanyImportStrategy;
        case 'contact_activities':
            return useContactActivityImportStrategy;
        case 'transactions':
            return useTransactionImportStrategy;
        case 'transaction_plans':
            return useRecurringPlanImportStrategy;
        case 'soft_credits':
            return useSoftCreditImportStrategy;
        case 'auction_items':
            return useAuctionItemImportStrategy;
        case 'funds':
            return useFundImportStrategy;
    }
};

const importFormatted = (importType: ImportType) => {
    switch (importType) {
        case 'contacts':
            return 'Contacts';
        case 'companies':
            return 'Companies';
        case 'contact_activities':
            return 'Contact activities';
        case 'transactions':
            return 'Transactions';
        case 'transaction_plans':
            return 'Recurring plans';
        case 'soft_credits':
            return 'Soft credits';
        case 'auction_items':
            return 'Auction items';
        case 'funds':
            return 'Funds';
    }
};

export { useImportStrategy, importFormatted };
